import { useEffect, useState, type ComponentType, type ReactNode } from 'react'
import { startRegistration } from '@simplewebauthn/browser'
import type { LucideProps } from 'lucide-react'
import {
  deletePasskey,
  listPasskeys,
  passkeyRegister,
  passkeyRegisterOptions,
  type AdminPasskey,
} from '../lib/api'

/**
 * Passkeys fürs Admin: Face ID, Fingerabdruck oder Sicherheitsschlüssel
 * statt Passwort tippen (Flo, 22.10.). Jeder Eintrag trägt einen Namen —
 * das Gerät zeigt ihn beim Anmelden, und im Prüfprotokoll steht damit, wer
 * etwas geändert hat. Ein Passkey gilt nur für diese Adresse (Production und
 * Test getrennt); das gemeinsame Passwort bleibt als Rückfall.
 */
export function PasskeysSection({
  Section,
  icon,
}: {
  Section: ComponentType<{
    title: string
    icon: ComponentType<LucideProps>
    tile: string
    glyph?: string
    footer?: ReactNode
    children: ReactNode
  }>
  icon: ComponentType<LucideProps>
}) {
  const [passkeys, setPasskeys] = useState<AdminPasskey[] | null>(null)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const unterstuetzt =
    typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined'

  async function laden() {
    try {
      setPasskeys((await listPasskeys()).passkeys)
    } catch {
      setPasskeys([])
    }
  }
  useEffect(() => {
    void laden()
  }, [])

  async function anlegen() {
    const n = name.trim()
    if (!n) return
    setBusy(true)
    setMeldung(null)
    try {
      const { options } = await passkeyRegisterOptions(n)
      const response = await startRegistration({ optionsJSON: options })
      await passkeyRegister(n, response)
      setName('')
      setMeldung({
        art: 'ok',
        text: `Passkey „${n}“ eingerichtet. Beim nächsten Anmelden reicht ein Blick aufs Gerät.`,
      })
      await laden()
    } catch (err) {
      const text = err instanceof Error ? err.message : String(err)
      setMeldung({
        art: 'fehler',
        text: /NotAllowedError|abgebrochen|cancel/i.test(text)
          ? 'Abgebrochen — kein Passkey angelegt.'
          : /InvalidStateError/i.test(text)
            ? 'Dieses Gerät hat hier schon einen Passkey.'
            : text,
      })
    } finally {
      setBusy(false)
    }
  }

  async function loeschen(p: AdminPasskey) {
    if (
      !window.confirm(
        `Passkey „${p.name}“ entfernen? Das Gerät kann sich dann nur noch mit dem Passwort anmelden.`,
      )
    )
      return
    setMeldung(null)
    try {
      await deletePasskey(p.id)
      await laden()
    } catch (err) {
      setMeldung({ art: 'fehler', text: err instanceof Error ? err.message : String(err) })
    }
  }

  const datum = (iso: string) =>
    new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })

  return (
    <Section
      title="Anmeldung"
      icon={icon}
      tile="#30d158"
      glyph="#0b2e14"
      footer="Ein Passkey gilt nur für diese Adresse — Production und Test haben je eigene. Das gemeinsame Passwort bleibt daneben bestehen. Der Name steht als Urheber im Prüfprotokoll."
    >
      <div className="space-y-4 px-5 py-4">
        <p className="text-sm text-text-muted">
          Mit einem Passkey meldest du dich per Face ID, Fingerabdruck oder Sicherheitsschlüssel an,
          ohne Passwort zu tippen. Lege ihn auf dem Gerät an, mit dem du dich anmelden willst.
        </p>
        {!unterstuetzt && (
          <p className="text-sm text-status-error">Dieser Browser unterstützt keine Passkeys.</p>
        )}
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            className="input flex-1"
            placeholder="Name, z. B. „Flo – iPhone“"
            aria-label="Name des Passkeys"
            maxLength={60}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void anlegen()
            }}
            disabled={busy || !unterstuetzt}
          />
          <button
            type="button"
            className="btn-primary shrink-0"
            onClick={() => void anlegen()}
            disabled={busy || !unterstuetzt || name.trim() === ''}
          >
            {busy ? 'Wartet aufs Gerät …' : 'Passkey auf diesem Gerät anlegen'}
          </button>
        </div>
        {meldung && (
          <p
            role={meldung.art === 'fehler' ? 'alert' : 'status'}
            className={`text-sm ${meldung.art === 'fehler' ? 'text-status-error' : 'text-status-active'}`}
          >
            {meldung.text}
          </p>
        )}
        {passkeys === null ? (
          <p className="text-sm text-text-muted">Lädt …</p>
        ) : passkeys.length === 0 ? (
          <p className="text-sm text-text-muted">Noch kein Passkey eingerichtet.</p>
        ) : (
          <ul className="divide-y divide-border rounded-xl border border-border">
            {passkeys.map((p) => (
              <li
                key={p.id}
                className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="font-medium">{p.name}</p>
                  <p className="text-xs text-text-muted">
                    Angelegt {datum(p.createdAt)}
                    {p.lastUsedAt
                      ? ` · zuletzt benutzt ${datum(p.lastUsedAt)}`
                      : ' · noch nie benutzt'}
                    {p.backedUp ? ' · auf mehreren Geräten (iCloud/Google)' : ''}
                  </p>
                </div>
                <button
                  type="button"
                  className="btn-chip-danger shrink-0"
                  onClick={() => void loeschen(p)}
                >
                  Entfernen
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Section>
  )
}
